import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import confetti from 'canvas-confetti'
import { Adult, Bags, CarCompany, CarName, Share, Success, Window } from '../../assests/ExportImageIcon'
// StarWhite was only used by the commented-out rating badge row
import { formatMoney } from '../../utils/formatMoney'
import service from '../../services/service'
import endpoints from '../../services/endpoints'
import { showToast } from '../../componants/toast/toastService'
import { getClientIp } from '../../utils/clientIp'
import { setCarConfirmation } from '../../redux/carRentalSearch/carRentalSearchSlice'
import CarSearchLoader from '../../componants/carSearchLoader/CarSearchLoader'

const IMPORTANT_INFO = [
    'Carry a valid government-issued photo ID',
    'Print or save your boarding pass on mobile',
    'Check baggage allowance before packing',
    'Reach airport at least 2 hours before departure',
]

const CarBookingConfirm = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const confirmation = useSelector((state) => state.carRentalSearch.confirmation)
    const selectedCar = useSelector((state) => state.carRentalSearch.selectedCar)
    const details = useSelector((state) => state.carRentalSearch.details)
    const bookingContext = useSelector((state) => state.carRentalSearch.bookingContext)
    const selectedInsurance = useSelector((state) => state.carRentalSearch.selectedInsurance)

    const [lookupId, setLookupId] = useState('')
    const [lookupLoading, setLookupLoading] = useState(false)
    // const [refreshLoading, setRefreshLoading] = useState(false) // Refresh Status button removed
    const [cancelLoading, setCancelLoading] = useState(false)
    const [invoiceDownloading, setInvoiceDownloading] = useState(false)
    const [ticketDownloading, setTicketDownloading] = useState(false)

    // Confetti rain (top -> bottom, across the full width) once a booking is
    // confirmed (skip for a cancelled one).
    useEffect(() => {
        if (!confirmation || (confirmation.booking_status || '').toUpperCase() === 'CANCELLED') return
        let animationFrameId
        const animationEnd = Date.now() + 5000
        const rainFrame = () => {
            confetti({
                particleCount: 5,
                startVelocity: 15,
                gravity: 0.7,
                spread: 60,
                angle: 90,
                ticks: 300,
                scalar: 1,
                origin: { x: Math.random(), y: -0.05 },
            })
            if (Date.now() < animationEnd) {
                animationFrameId = requestAnimationFrame(rainFrame)
            }
        }
        rainFrame()
        return () => {
            if (animationFrameId) cancelAnimationFrame(animationFrameId)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [confirmation])

    const handleLookup = async () => {
        if (!lookupId.trim()) {
            showToast('Please enter a booking confirmation ID', 'error')
            return
        }
        setLookupLoading(true)
        try {
            const ip = await getClientIp()
            const { data } = await service.post(endpoints.cars.bookingDetails, {
                ip_address: ip,
                confirmation_id: lookupId.trim(),
            }, { timeout: 45000 })
            if (!data.data?.confirmation_number) {
                showToast('No booking found for this confirmation ID', 'error')
                return
            }
            dispatch(setCarConfirmation(data.data))
        } catch {
            // error toast already shown by the service interceptor
        } finally {
            setLookupLoading(false)
        }
    }

    if (!confirmation) {
        return (
            <section className='bookingConfirm commonTopPadding commonBottomPadding'>
                <div className='container'>
                    <p>No booking found. Please complete a booking first, or look up an existing one below.</p>

                    <div className='bsSection'>
                        <div className='bsSectionTitle'>Find Your Booking</div>
                        <div className='bsBox'>
                            <div className='travellerFieldRow'>
                                <div className='travellerField'>
                                    <label>Confirmation ID</label>
                                    <input type='text' value={lookupId} onChange={(e) => setLookupId(e.target.value)} placeholder='e.g. AE850762950' />
                                </div>
                            </div>
                            {lookupLoading ? (
                                <CarSearchLoader size='compact' text='Looking up your booking...' />
                            ) : (
                                <button type='button' className='commonBtn' onClick={handleLookup}>Find Booking</button>
                            )}
                        </div>
                    </div>

                    <button type='button' className='commonBtn' onClick={() => navigate('/car-list')}>Search Cars</button>
                </div>
            </section>
        )
    }

    const rental = confirmation.rental || {}
    const carDetails = rental.car_details || {}
    const fee = rental.fee || {}
    const pax = confirmation.pax_details || {}
    const payment = confirmation.payment_method || {}
    const status = (confirmation.booking_status || '').toUpperCase()
    const isCancelled = status === 'CANCELLED'

    // Same car detail as car-detail.jsx: prefer the actual booked car (carDetails,
    // from /cars/book), fall back to /cars/details, then the search result —
    // staging often returns null car_details on /cars/book, so this keeps the
    // card from going blank.
    const apiCarDetails = details?.car_details
    const CAR_BOOKING = {
        bookingId: confirmation.confirmation_number || '—',
        rating: 4.5,
        brand: CarCompany,
        title: carDetails.car_name || apiCarDetails?.name || selectedCar?.car_details?.car_name || 'Car',
        carCode: carDetails.car_code || apiCarDetails?.code || selectedCar?.car_details?.car_code || '',
        transmission: carDetails.transmission_type || apiCarDetails?.transmission || selectedCar?.car_details?.transmission_type || '',
        adults: carDetails.passenger_quantity ?? apiCarDetails?.passengers ?? selectedCar?.car_details?.passenger_quantity ?? '-',
        bags: carDetails.baggage_quantity ?? apiCarDetails?.baggage ?? selectedCar?.car_details?.baggage_quantity ?? '-',
        windows: selectedCar?.car_details?.vehicle_door ?? '-',
        fuelType: carDetails.fuel_type || apiCarDetails?.fuel_type || '-',
        airConditioning: carDetails.air_conditioning === true || carDetails.air_conditioning === 'true'
            ? 'Yes'
            : (carDetails.air_conditioning === false || carDetails.air_conditioning === 'false' ? 'No' : '-'),
        image: carDetails.car_image || apiCarDetails?.image_url || selectedCar?.car_details?.car_image_hd || selectedCar?.car_details?.car_image || CarName,
        route: `${rental.pickup?.location_name || ''} → ${rental.dropoff?.location_name || ''}`,
        date: confirmation.booking_date || rental.pickup?.date_time || '',
        passengers: `${confirmation.no_of_passenger || 1} Adult${Number(confirmation.no_of_passenger) > 1 ? 's' : ''}`,
    }

    const PICKUP_DROPOFF = [
        { label: 'Pick-up', point: rental.pickup },
        { label: 'Drop-off', point: rental.dropoff },
    ]

    const CONTACT_DETAILS = [
        { label: 'Full Name:', value: confirmation.customer_name || `${pax.title || ''} ${pax.first_name || ''} ${pax.last_name || ''}`.trim() },
        { label: 'Email ID:', value: confirmation.customer_email || pax.email_id },
        { label: 'Phone Number:', value: confirmation.customer_phone || pax.phone },
    ]

    const PASSENGERS_ALL = [
        {
            name: `${pax.title || ''} ${pax.first_name || ''} ${pax.last_name || ''}`.trim() || confirmation.customer_name,
            type: 'Primary Driver',
            addressLine: [pax.address, pax.city, pax.state, pax.pincode].filter(Boolean).join(', '),
        },
    ]

    // Exact amounts actually paid at booking time: prefer the /cars/book response
    // (fee.*), fall back to what was shown on the payment page (bookingContext.fare_summary,
    // selectedInsurance, selectedCar.car_details) — staging often returns null fee.* fields.
    const paymentCurrency = fee.currency_code || bookingContext?.currency || selectedCar?.car_details?.currency_code
    // Rental Amount = vehicle + coverage charge only (NOT total_fare, which already
    // includes extras) — otherwise extras get double-counted against the Extras row below.
    const rentalAmount = fee.rate_total_amount ?? (
        bookingContext?.fare_summary
            ? (Number(bookingContext.fare_summary.vehicle_charge) || 0) + (Number(bookingContext.fare_summary.coverage_charge) || 0)
            : null
    )
    const insuranceAmount = fee.insurance_amount ?? selectedInsurance?.plan_cost_amount ?? null
    const extrasAmount = fee.extras_amount ?? bookingContext?.fare_summary?.optional_extra_charge ?? null
    const estimatedDeposit = fee.estimated_deposit ?? selectedCar?.car_details?.estimated_deposit ?? null
    const remainingAmount = fee.remaining_amount ?? selectedCar?.car_details?.remaining_amount ?? null

    const PAYMENT_DETAILS = [
        { label: 'Rental Amount', value: formatMoney(rentalAmount, paymentCurrency) },
        { label: 'Insurance', value: formatMoney(insuranceAmount, paymentCurrency) },
        { label: 'Extras', value: formatMoney(extrasAmount, paymentCurrency) },
        { label: 'Estimated Deposit', value: formatMoney(estimatedDeposit, paymentCurrency) },
        { label: 'Remaining Amount', value: formatMoney(remainingAmount, paymentCurrency) },
    ]

    // TOTAL AMOUNT = sum of the itemized rows above (rental + insurance + extras),
    // since the API doesn't return a single grand-total field.
    const totalPaid = (Number(rentalAmount) || 0) + (Number(insuranceAmount) || 0) + (Number(extrasAmount) || 0)

    const PAYMENT_META = [
        { label: 'Payment Method', value: payment.card_code ? `${payment.card_code} •••• ${payment.last4}` : '—' },
        { label: 'Card Holder', value: payment.card_holder_name || '—' },
        { label: 'Payment Date', value: confirmation.booking_date || '—' },
    ]

    // Refresh Status button removed from this page.
    // const handleRefreshStatus = async () => {
    //     if (!confirmation.confirmation_number) return
    //     setRefreshLoading(true)
    //     try {
    //         const ip = await getClientIp()
    //         const { data } = await service.post(endpoints.cars.bookingDetails, {
    //             ip_address: ip,
    //             confirmation_id: confirmation.confirmation_number,
    //         }, { timeout: 45000 })
    //         dispatch(setCarConfirmation({ ...confirmation, ...data.data }))
    //         showToast('Booking status refreshed', 'success')
    //     } catch {
    //         // error toast already shown by the service interceptor
    //     } finally {
    //         setRefreshLoading(false)
    //     }
    // }

    const handleDownloadInvoice = async () => {
        if (!confirmation.booking_id || invoiceDownloading) return
        setInvoiceDownloading(true)
        try {
            const url = endpoints.cars.invoice.replace('{booking_id}', confirmation.booking_id)
            const response = await service.download(url)
            const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
            const link = document.createElement('a')
            link.href = blobUrl
            link.download = `car-invoice-SAB${confirmation.confirmation_number || confirmation.booking_id}.pdf`
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(blobUrl)
        } catch {
            // error toast already shown by the service interceptor
        } finally {
            setInvoiceDownloading(false)
        }
    }

    const handleDownloadTicket = async () => {
        if (!confirmation.booking_id || ticketDownloading) return
        setTicketDownloading(true)
        try {
            const url = endpoints.cars.ticket.replace('{booking_id}', confirmation.booking_id)
            const response = await service.download(url)
            const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
            const link = document.createElement('a')
            link.href = blobUrl
            link.download = `car-ticket-${confirmation.confirmation_number || confirmation.booking_id}.pdf`
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(blobUrl)
        } catch {
            // error toast already shown by the service interceptor
        } finally {
            setTicketDownloading(false)
        }
    }

    const handleCancelBooking = async () => {
        if (!confirmation.confirmation_number || isCancelled) return
        setCancelLoading(true)
        try {
            const ip = await getClientIp()
            const { data } = await service.post(endpoints.cars.cancel, {
                ip_address: ip,
                confirmation_id: confirmation.confirmation_number,
            }, { timeout: 45000 })
            dispatch(setCarConfirmation({
                ...confirmation,
                booking_status: data.data?.status || 'CANCELLED',
                customer_name: data.data?.customer_name || confirmation.customer_name,
            }))
            showToast('Booking cancelled', 'success')
        } catch {
            // error toast already shown by the service interceptor
        } finally {
            setCancelLoading(false)
        }
    }

    return (
        <>
            <section className='bookingConfirm commonTopPadding commonBottomPadding'>
                <div className='container'>
                    <div className='row g-4'>
                        <div className='col-md-12'>
                            <div className='bookingTextConfirm'>
                                <img src={Success} />
                                <div className='bookingHead commonGredientText'>{isCancelled ? 'Booking Cancelled' : 'Booking Confirmed'}</div>
                                <p>{isCancelled ? 'Your car booking has been cancelled' : 'Your Car has been successfully booked'}</p>
                            </div>
                        </div>
                        <div className='col-md-12'>
                            <div className='bookingConfirmInner'>
                                <div className='resultCard'>
                                    <div className='bookingConfirmMeta'>
                                        <div className='bookingConfirmMetaLabel'>Booking ID <strong>{CAR_BOOKING.bookingId}</strong></div>
                                        {status && (
                                            <span className={`carBookingStatusBadge ${isCancelled ? 'carBookingStatusBadge--cancelled' : ''}`}>{status}</span>
                                        )}
                                    </div>

                                    {/* <div className='carResultCard__top mt-3'>
                                        <span className='hotelCard__ratingBadge'>
                                            <img src={StarWhite} alt="" /> {CAR_BOOKING.rating}
                                        </span>
                                        <img src={CAR_BOOKING.brand} alt="" className='carResultCard__brand' />
                                    </div> */}

                                    <img src={CAR_BOOKING.image} alt={CAR_BOOKING.title} className='carResultCard__image bookingCarTime' />

                                    <div className='carResultCard__title'>{CAR_BOOKING.title}{CAR_BOOKING.carCode ? ` (${CAR_BOOKING.carCode})` : ''}</div>
                                    <div className='carResultCard__subtitle'>{CAR_BOOKING.transmission}</div>

                                    <div className='carResultCard__specs'>
                                        <span><img src={Adult} alt="" /> {CAR_BOOKING.adults} Adults</span>
                                        <span><img src={Bags} alt="" /> {CAR_BOOKING.bags} Bags</span>
                                        <span><img src={Window} alt="" /> {CAR_BOOKING.windows} Doors</span>
                                    </div>

                                    <div className='bsFareDivider' />

                                    <div className='bsFareRow'>
                                        <span>Route:</span>
                                        <span>{CAR_BOOKING.route}</span>
                                    </div>
                                    <div className='bsFareRow'>
                                        <span>Date:</span>
                                        <span>{CAR_BOOKING.date}</span>
                                    </div>
                                    <div className='bsFareRow'>
                                        <span>Passengers:</span>
                                        <span>{CAR_BOOKING.passengers}</span>
                                    </div>
                                    <div className='bsFareRow'>
                                        <span>Fuel Type:</span>
                                        <span>{CAR_BOOKING.fuelType}</span>
                                    </div>
                                    <div className='bsFareRow'>
                                        <span>Air Conditioning:</span>
                                        <span>{CAR_BOOKING.airConditioning}</span>
                                    </div>

                                    <div className='bookingConfirmActions'>
                                        <button type='button' className='bookingDownloadBtn' onClick={handleDownloadInvoice} disabled={invoiceDownloading}>
                                            {invoiceDownloading ? 'Downloading...' : 'Download Invoice'}
                                        </button>
                                        <button type='button' className='bookingShareBtn' aria-label="Share">
                                            <img src={Share} alt="" />
                                        </button>
                                    </div>

                                    <div className='bookingConfirmActions mt-2'>
                                        <button type='button' className='bookingDownloadBtn' onClick={handleDownloadTicket} disabled={ticketDownloading}>
                                            {ticketDownloading ? 'Downloading...' : 'Download Ticket'}
                                        </button>
                                        {!isCancelled && (
                                            <button type='button' className='bookingDownloadBtn tripDocCancelBtn' onClick={handleCancelBooking} disabled={cancelLoading}>
                                                {cancelLoading ? 'Cancelling...' : 'Cancel Booking'}
                                            </button>
                                        )}
                                    </div>

                                    <button
                                        type='button'
                                        className='commonBtnTransparent w-100 mt-2'
                                        onClick={() => navigate('/profile', { state: { section: 'create-group' } })}
                                    >
                                        Create Splitwise Group
                                    </button>
                                </div>

                                <div className='bsSection'>
                                    <div className='bsSectionTitle'>Contact Details</div>
                                    <div className='bsBox'>
                                        {CONTACT_DETAILS.map((item, index) => (
                                            <div className='bsFareRow' key={index}>
                                                <span>{item.label}</span>
                                                <span>{item.value}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className='bsSection'>
                                    <div className='bsSectionTitle'>Passenger Detail</div>
                                    <div className='bsBox'>
                                        {PASSENGERS_ALL.map((passenger, index) => (
                                            <div className='bookingGuestCard' key={index}>
                                                <div className='bsFareRow'>
                                                    <span>Passenger {index + 1}</span>
                                                    {/* <span>{passenger.type}</span> */}
                                                </div>
                                                <div className='hotelCard__name'>{passenger.name}</div>
                                                {passenger.addressLine && (
                                                    <div className='bsFareRow'>
                                                        <span>Address:</span>
                                                        <span>{passenger.addressLine}</span>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className='bsSection'>
                                    <div className='bsSectionTitle'>Pickup &amp; Drop-off Details</div>
                                    <div className='bsBox'>
                                        {PICKUP_DROPOFF.map((item, index) => (
                                            <div key={item.label}>
                                                {index > 0 && <div className='bsFareDivider' />}
                                                <div className='bsSectionTitle fareBaggageTitle'>{item.label}</div>
                                                <div className='bsFareRow'>
                                                    <span>Location:</span>
                                                    <span>{item.point?.location_name || '-'}</span>
                                                </div>
                                                <div className='bsFareRow'>
                                                    <span>Address:</span>
                                                    <span>{item.point?.address || '-'}</span>
                                                </div>
                                                <div className='bsFareRow'>
                                                    <span>Date &amp; Time:</span>
                                                    <span>{item.point?.date_time || '-'}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className='bsSection'>
                                    <div className='bsSectionTitle'>Confirmation Sent</div>
                                    <div className='bsBox'>
                                        <p className='bookingConfirmSentText'>
                                            We&apos;ve sent your booking confirmation via email, SMS and WhatsApp.
                                        </p>
                                    </div>
                                </div>

                                <div className='bsSection'>
                                    <div className='bsSectionTitle'>Payment Details</div>
                                    <div className='bsBox'>
                                        {PAYMENT_DETAILS.map((item, index) => (
                                            <div className='bsFareRow' key={index}>
                                                <span>{item.label}</span>
                                                <span>{item.value}</span>
                                            </div>
                                        ))}
                                        <div className='bsFareDivider' />
                                        <div className='bsFareRow bsFareRow--total'>
                                            <span>TOTAL AMOUNT</span>
                                            <span>{formatMoney(totalPaid, paymentCurrency)}</span>
                                        </div>
                                        <div className='bsFareDivider' />
                                        {PAYMENT_META.map((item, index) => (
                                            <div className='bsFareRow' key={index}>
                                                <span>{item.label}</span>
                                                <span>{item.value}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {confirmation.warning?.length > 0 && (
                                    <div className='bsSection'>
                                        <div className='bsSectionTitle'>Warnings</div>
                                        <div className='bsBox'>
                                            <ul className='bsInfoList'>
                                                {confirmation.warning.map((info, index) => (
                                                    <li key={index}>{info}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                )}

                                <div className='bsSection'>
                                    <div className='bsSectionTitle'>Important Information</div>
                                    <div className='bsBox'>
                                        <ul className='bsInfoList'>
                                            {IMPORTANT_INFO.map((info, index) => (
                                                <li key={index}>{info}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>


                </div>
            </section>
        </>
    )
}

export default CarBookingConfirm
